import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Business } from '../types';
import { db } from '../Firebase/firebase';
import { collection, getDocs } from 'firebase/firestore';

export interface BusinessListState {
  businesses: Business[];
  selectedBusiness: Business | null;
  filterOptions: ('New' | 'Active' | 'Closed')[];
  showFilterMenu: boolean;
  businessSearchText: string;
  isFetchingBusinesses: boolean;
  errorFetchingBusinesses: boolean;
  addingBusiness: boolean;
  editingBusiness: boolean;
  webModalShown: 'None' | 'Add_Business' | 'Edit_Business';
}

const initialState: BusinessListState = {
  businesses: [],
  selectedBusiness: null,
  filterOptions: [],
  showFilterMenu: false,
  businessSearchText: '',
  isFetchingBusinesses: false,
  errorFetchingBusinesses: false,
  addingBusiness: false,
  editingBusiness: false,
  webModalShown: 'None',
};

export const fetchBusinesses = createAsyncThunk(
  'businesses/fetchAllBusinesses',
  async () => {
    const businessCollectionRef = collection(db, 'businesses');
    const data = await getDocs(businessCollectionRef);
    const businesses = data.docs.map((doc) => {
      const data = doc.data();
      const id = doc.id;
      return { id, ...data } as Business;
    });
    return businesses;
  }
);

const businessList = createSlice({
  name: 'businessList',
  initialState,
  reducers: {
    setSelectedBusiness: (state, action: PayloadAction<Business | null>) => {
      state.selectedBusiness = action.payload;
    },

    setBusinesses: (state, action: PayloadAction<Business[]>) => {
      state.businesses = action.payload;
    },
    setAddingBusiness: (state, action: PayloadAction<boolean>) => {
      state.addingBusiness = action.payload;
    },
    setEditingBusiness: (state, action: PayloadAction<boolean>) => {
      state.editingBusiness = action.payload;
    },
    setFilterOptions: (
      state,
      action: PayloadAction<'New' | 'Active' | 'Closed'>
    ) => {
      const option = action.payload;

      if (state.filterOptions.includes(option)) {
        const index = state.filterOptions.indexOf(option);

        state.filterOptions = [
          ...state.filterOptions.slice(0, index),
          ...state.filterOptions.slice(index + 1),
        ];
      } else {
        state.filterOptions = [...state.filterOptions, option];
      }
    },
    toggleFilterMenu: (state) => {
      state.showFilterMenu = !state.showFilterMenu;
    },
    setBusinessSearchText: (state, action: PayloadAction<string>) => {
      state.businessSearchText = action.payload;
    },
    setWebModalShown: (
      state,
      action: PayloadAction<'None' | 'Add_Business' | 'Edit_Business'>
    ) => {
      state.webModalShown = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBusinesses.pending, (state) => {
      state.isFetchingBusinesses = true;
    });

    builder.addCase(fetchBusinesses.fulfilled, (state, action) => {
      state.businesses = action.payload;
      state.isFetchingBusinesses = false;
    });

    builder.addCase(fetchBusinesses.rejected, (state) => {
      state.errorFetchingBusinesses = true;
    });
  },
});

export const {
  setBusinesses,
  setFilterOptions,
  toggleFilterMenu,
  setBusinessSearchText,
  setSelectedBusiness,
  setAddingBusiness,
  setEditingBusiness,
  setWebModalShown,
} = businessList.actions;

export default businessList.reducer;
